import type { MeetingRoom, CalendarEvent } from "@/types/calendar";
import { RoomColumn, type MoveDragGhost } from "./RoomColumn";
import { CurrentTimeLine } from "./CurrentTimeLine";
import { CALENDAR_CONFIG } from "@/config/rooms";
import {
  getHourLabels,
  getCurrentTimePosition,
  isWithinDisplayHours,
} from "@/lib/calendar-utils";

interface TimelineGridProps {
  rooms: MeetingRoom[];
  eventsMap: Map<string, CalendarEvent[]>;
  currentTime: Date;
  gridHeight: number;
  onEventClick?: (event: CalendarEvent, room: MeetingRoom) => void;
  onSlotSelect?: (roomId: string, startISO: string, endISO: string) => void;
  registerColumnRef?: (roomId: string, el: HTMLDivElement | null) => void;
  moveDragTargetRoomId?: string | null;
  moveDragGhost?: MoveDragGhost | null;
  draggingEventId?: string | null;
  isMovableEvent?: (event: CalendarEvent) => boolean;
  onMoveDragStart?: (
    event: CalendarEvent,
    fromRoomId: string,
    e: React.PointerEvent<HTMLDivElement>,
  ) => void;
}

export function TimelineGrid({
  rooms,
  eventsMap,
  currentTime,
  gridHeight,
  onEventClick,
  onSlotSelect,
  registerColumnRef,
  moveDragTargetRoomId,
  moveDragGhost,
  draggingEventId,
  isMovableEvent,
  onMoveDragStart,
}: TimelineGridProps) {
  const hourLabels = getHourLabels();
  const currentTimePosition = getCurrentTimePosition(currentTime);
  const showCurrentLine = isWithinDisplayHours(currentTime);

  return (
    <div className="relative flex" style={{ height: gridHeight }}>
      {/* Hour labels column */}
      <div className="w-16 md:w-20 shrink-0 relative">
        {hourLabels.map(({ hour, label }, index) => (
          <div
            key={hour}
            className="absolute right-2 text-xs text-muted-foreground -translate-y-1/2 font-mono"
            style={{ top: index * CALENDAR_CONFIG.hourHeight }}
          >
            {label}
          </div>
        ))}
      </div>

      {/* Room columns container */}
      <div className="flex-1 flex relative">
        {/* Hour grid lines */}
        {hourLabels.map(({ hour }, index) => (
          <div
            key={hour}
            className="absolute left-0 right-0 border-t border-border/30"
            style={{ top: index * CALENDAR_CONFIG.hourHeight }}
          />
        ))}

        {/* Half-hour grid lines (lighter) */}
        {hourLabels.slice(0, -1).map(({ hour }, index) => (
          <div
            key={`half-${hour}`}
            className="absolute left-0 right-0 border-t border-border/15 border-dashed"
            style={{
              top: index * CALENDAR_CONFIG.hourHeight + CALENDAR_CONFIG.hourHeight / 2,
            }}
          />
        ))}

        {/* Room columns */}
        {rooms.map((room, index) => (
          <RoomColumn
            key={room.id}
            room={room}
            events={eventsMap.get(room.id) || []}
            isFirst={index === 0}
            currentTime={currentTime}
            onEventClick={onEventClick}
            onSlotSelect={onSlotSelect}
            registerColumnRef={registerColumnRef}
            moveDragGhost={moveDragTargetRoomId === room.id ? moveDragGhost : null}
            draggingEventId={draggingEventId}
            isMovableEvent={isMovableEvent}
            onMoveDragStart={onMoveDragStart}
          />
        ))}

        {/* Current time indicator */}
        {showCurrentLine && (
          <CurrentTimeLine position={currentTimePosition} currentTime={currentTime} />
        )}
      </div>
    </div>
  );
}
